// Configuration des types de clients : liste de libellés (cards)

import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Popconfirm,
  Typography,
  Modal,
  Form,
  Input,
  Divider,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { useAppTranslation } from '../../../hooks/useAppTranslation';

const { Text } = Typography;

interface ClientTypesStepProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  showDivider?: boolean;
  description?: string;
  /** Titre de la card (défaut : Types de clients) */
  title?: string;
  addButtonLabel?: string;
  modalAddTitle?: string;
  modalEditTitle?: string;
  inputPlaceholder?: string;
  emptyText?: string;
  deleteConfirmTitle?: string;
  /** Libellé du bouton OK du modal */
  modalOkText?: string;
  fieldLabel?: string;
  fieldRequired?: string;
  fieldMax?: string;
  /** Désactive les actions pendant une écriture Firestore. */
  saving?: boolean;
}

const ClientTypesStep: React.FC<ClientTypesStepProps> = ({
  value = [],
  onChange,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
  showDivider = true,
  description,
  title,
  addButtonLabel,
  modalAddTitle,
  modalEditTitle,
  inputPlaceholder,
  emptyText,
  deleteConfirmTitle,
  modalOkText,
  fieldLabel,
  fieldRequired,
  fieldMax,
  saving = false,
}) => {
  const { t } = useAppTranslation(['activity', 'common']);
  const resolvedTitle = title ?? t('clientTypes.title');
  const resolvedDescription = description ?? t('clientTypes.stepDescription');
  const resolvedAdd = addButtonLabel ?? t('common:actions.add');
  const resolvedModalAdd = modalAddTitle ?? t('clientTypes.modalAdd');
  const resolvedModalEdit = modalEditTitle ?? t('clientTypes.modalEdit');
  const resolvedPlaceholder = inputPlaceholder ?? t('clientTypes.inputPlaceholder');
  const resolvedEmpty = emptyText ?? t('clientTypes.empty');
  const resolvedDeleteConfirm = deleteConfirmTitle ?? t('clientTypes.deleteConfirm');
  const resolvedFieldLabel = fieldLabel ?? t('clientTypes.fieldLabel');
  const resolvedFieldRequired = fieldRequired ?? t('clientTypes.fieldRequired');
  const resolvedFieldMax = fieldMax ?? t('clientTypes.fieldMax');

  const [clientTypes, setClientTypes] = useState<string[]>(value);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [form] = Form.useForm<{ label: string }>();

  useEffect(() => {
    setClientTypes(value ?? []);
  }, [value]);

  const handleChange = (newList: string[]) => {
    setClientTypes(newList);
    onChange?.(newList);
  };

  const handleAdd = () => {
    setEditingIndex(null);
    form.resetFields();
    form.setFieldsValue({ label: '' });
    setModalOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    form.setFieldsValue({ label: clientTypes[index] });
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const label = (values.label as string)?.trim();
      if (!label) return;

      let newList: string[];
      if (editingIndex !== null) {
        newList = clientTypes.map((s, i) => (i === editingIndex ? label : s));
      } else {
        newList = [...clientTypes, label];
      }
      handleChange(newList);
      setModalOpen(false);
      setEditingIndex(null);
      form.resetFields();
    });
  };

  const handleDelete = (index: number) => {
    const newList = clientTypes.filter((_, i) => i !== index);
    handleChange(newList);
  };

  const showActions = canUpdate || canDelete;

  return (
    <>
      {showDivider && <Divider />}
      <Card
        size="small"
        title={
          <Space>
            <TeamOutlined />
            <span>{resolvedTitle}</span>
          </Space>
        }
        extra={
          canCreate ? (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              disabled={saving}
            >
              {resolvedAdd}
            </Button>
          ) : null
        }
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          {resolvedDescription}
        </Text>
        {clientTypes.length === 0 ? (
          <Text type="secondary">{resolvedEmpty}</Text>
        ) : (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {clientTypes.map((label, index) => (
              <Card key={`${label}-${index}`} size="small" styles={{ body: { padding: '10px 12px' } }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Text strong>{label}</Text>
                  {showActions && (
                    <Space>
                      {canUpdate && (
                        <Button
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(index)}
                          disabled={saving}
                        />
                      )}
                      {canDelete && (
                        <Popconfirm
                          title={resolvedDeleteConfirm}
                          onConfirm={() => handleDelete(index)}
                          okText={t('common:actions.delete')}
                          cancelText={t('common:actions.cancel')}
                          disabled={saving}
                        >
                          <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={saving}
                          />
                        </Popconfirm>
                      )}
                    </Space>
                  )}
                </div>
              </Card>
            ))}
          </Space>
        )}
      </Card>

      <Modal
        title={editingIndex !== null ? resolvedModalEdit : resolvedModalAdd}
        open={modalOpen}
        onOk={handleSave}
        confirmLoading={saving}
        onCancel={() => {
          setModalOpen(false);
          setEditingIndex(null);
          form.resetFields();
        }}
        okText={
          modalOkText ??
          (editingIndex !== null ? t('common:actions.edit') : t('common:actions.add'))
        }
        cancelText={t('common:actions.cancel')}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="label"
            label={resolvedFieldLabel}
            rules={[
              { required: true, message: resolvedFieldRequired },
              { max: 80, message: resolvedFieldMax },
            ]}
          >
            <Input placeholder={resolvedPlaceholder} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ClientTypesStep;
